/**
 * RelayDeviceComponent
 * - display relay device status
 * - display device info
 * - control on/off status
 */

import React, { useEffect, useState } from 'react';

/* MUI */
import { Avatar, Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

/* Icons */
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/* Types */
import {
  RelayDevice,
  useRelayDeviceDetailQuery,
  useRelayDeviceDetailUpdateSubscription,
  useTurnRelayDeviceOnMutation,
  useTurnRelayDeviceOffMutation,
} from '../../../../types/generated-types';

/* Shared */
import { DeviceInfo, DeviceInformation } from '../shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../shared-ui/alerts-info';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import moment from 'moment/moment';
import { useDeviceHasAlerts } from '../../../system/AlertsManager';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';

interface RelayDeviceProps {
  deviceId: string;
}

type UpdatingState = 'Updating' | 'Awaiting' | 'Blocked' | 'Idle';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function RelayDeviceComponent({ deviceId }: RelayDeviceProps) {
  const [device, setDevice] = useState<Partial<RelayDevice>>();
  const { data, loading, error } = useRelayDeviceDetailQuery({
    variables: { id: deviceId },
  });
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);
  const [updatingState, setUpdatingState] = useState<UpdatingState>('Idle');
  const [turnRelayDeviceOn] = useTurnRelayDeviceOnMutation();
  const [turnRelayDeviceOff] = useTurnRelayDeviceOffMutation();
  const { can } = useAuthorizer();

  useEffect(() => {
    if (data?.relayDeviceById) {
      setDevice(data.relayDeviceById as any);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const panelInfo: DeviceInformation = {
        Zone: device.zone?.name ?? 'Unnamed',
        'Zone ID': device.zoneId ?? 'Unidentified',
        Source: device.source?.name ?? 'Unnamed source',
      };

      const devicesInfo: DeviceInformation[] = [
        {
          'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
          Name: device.name ?? 'Unnamed',
          Type: device.typeDisplayName ?? 'Unknown',
          firmwareInfo: {
            Model:
              device.meta?.model || device.meta?.modelNumber || 'Unknown model',
            Version: device.meta?.version || 'Unknown version',
          },
          Status: device.isOn?.value ? 'On' : 'Off',
          'Last Updated': moment(device.timestamp).fromNow(),
        },
      ];

      setDeviceInfo([panelInfo, ...devicesInfo]);
    }
  }, [device]);

  useRelayDeviceDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (device?.hasPendingUpdates) {
      if (updatingState === 'Idle') {
        setUpdatingState('Blocked');
        Notifier.warn('Someone else is updating this device.');
      } else if (updatingState === 'Updating') {
        setUpdatingState('Awaiting');
      }
    } else {
      if (updatingState === 'Updating' || updatingState === 'Blocked') {
        setUpdatingState('Idle');
      } else if (updatingState === 'Awaiting') {
        setUpdatingState('Idle');
        Notifier.success('Device update completed.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.hasPendingUpdates]);

  const [expanded, setExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const hasAlerts = useDeviceHasAlerts(device);

  const handleOnOffToggleChange = async (checked: boolean) => {
    if (!device || !device.deviceId) {
      return;
    }

    try {
      setUpdatingState('Updating');
      if (checked) {
        await turnRelayDeviceOn({ variables: { deviceId: device.deviceId } });
      } else {
        await turnRelayDeviceOff({ variables: { deviceId: device.deviceId } });
      }
    } catch (updateError) {
      setUpdatingState('Idle');
      console.error('Error updating device state:', updateError);
      Notifier.error('Failed to update relay device state.');
    }
  };

  if (error) {
    console.error('RelayDevice query error:', error);
    return (
      <div>
        Error loading relay device: {error.message || 'Unknown error'}
        {error.graphQLErrors?.map((err, idx) => (
          <div key={idx}>{err.message}</div>
        ))}
      </div>
    );
  }

  return loading ? (
    <DeviceLoadingSkeleton />
  ) : device ? (
    <Card
      sx={{
        backgroundColor: '#ebf7f6',
        boxShadow: 1,
        p: 0,
        width: '100%',
        border: hasAlerts ? '2px solid red' : ' ',
        borderRadius: '4px',
        marginTop: '4px',
      }}
    >
      <CardHeader
        sx={{
          paddingTop: '0px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '0px',
          border: '1px solid rgba(0,0,0, 0.1)',
        }}
        title={
          <div
            style={{
              height: '40px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                flex: 'auto',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  padding: '0px',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{
                      display: 'flex',
                      flex: '1',
                      flexDirection: 'column',
                    }}
                  >
                    {device?.name}
                  </Typography>
                </div>
              </Box>
            </div>
            <div style={{ flex: '1 1 auto', flexDirection: 'column' }} />
            <div
              style={{
                flex: '0 0 auto',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  p: 0,
                  backgroundColor: device.isOn?.value
                    ? 'lightgreen'
                    : 'lightgrey',
                  height: '100%',
                  paddingTop: '8px',
                }}
              >
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <div style={{ marginLeft: '6px' }}>&nbsp;</div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {!['Updating', 'Awaiting', 'Blocked'].includes(
                      updatingState,
                    ) ? (
                      can('control', device) ? (
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={device.isOn?.value ?? false}
                              onChange={(e) =>
                                handleOnOffToggleChange(e.target.checked)
                              }
                              disabled={
                                (device.hasPendingUpdates ?? false) ||
                                ['Updating', 'Awaiting', 'Blocked'].includes(
                                  updatingState,
                                )
                              }
                            />
                          }
                          label={device.isOn?.value ? 'On' : 'Off'}
                          sx={{
                            paddingLeft: '10px',
                            paddingRight: '10px',
                            margin: 0,
                          }}
                        />
                      ) : (
                        <Typography
                          variant="h4"
                          sx={{
                            paddingLeft: '10px',
                            paddingRight: '10px',
                          }}
                        >
                          {device.isOn?.value ? 'On' : 'Off'}
                        </Typography>
                      )
                    ) : null}
                    {['Updating', 'Awaiting', 'Blocked'].includes(
                      updatingState,
                    ) && (
                      <CircularProgress size={16} sx={{ marginLeft: '4px' }} />
                    )}
                    {['Awaiting', 'Blocked'].includes(updatingState) && (
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'text.secondary',
                          marginLeft: '4px',
                        }}
                      >
                        Updating...
                      </Typography>
                    )}
                  </div>
                  <div style={{ marginRight: '0' }}>&nbsp;</div>
                </Stack>
              </Box>
            </div>
          </div>
        }
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              padding: '0px',
              margin: '0px',
              marginRight: '4px',
            }}
          >
            {hasAlerts ? (
              <ToggleAlertDetailsButton
                device={device}
                expand={alertsExpanded}
                onClick={(event) => {
                  event.preventDefault();
                  setAlertsExpanded(!alertsExpanded);
                }}
                buttonLabel="Show alerts"
              />
            ) : (
              <PowerSettingsNewIcon
                sx={{
                  color: 'darkgreen',
                  margin: '0 6px 0 0',
                }}
              />
            )}
          </Avatar>
        }
        action={
          <Box>
            <div
              style={{
                padding: 0,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <ExpandMore
                expand={expanded}
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="show more"
                sx={{ padding: '0px', margin: '0px' }}
              >
                <ExpandMoreIcon sx={{ padding: '0px', margin: '0px' }} />
              </ExpandMore>
            </div>
          </Box>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <DeviceInfo infoEntries={deviceInfo} />
      </Collapse>
      <DeviceAlerts device={device} alertsExpanded={alertsExpanded} />
    </Card>
  ) : null;
}
